import { CatalogicalItem } from "common/types/catalogical-type";
import { RuleProperties } from "json-rules-engine";
import { getItemRules, RulesEngineFactory } from "lib/helpers/rules-engine-factory";
import { AmazonianInformation } from "lib/models/oui-models";

// Re-export getItemRules for backward compatibility
export { getItemRules };

const SHOW_VERSION_RULE_NAME = "show-version";

/**
 * Checks if a set of rules contains a "show-version" rule
 * @param rules - Array of RuleProperties to check
 * @returns true if any rule has the name "show-version"
 */
export const hasShowVersionRule = (rules: RuleProperties[]): boolean => {
  return rules.some((rule) => rule.name === SHOW_VERSION_RULE_NAME);
};

/**
 * Synchronously checks if any version in an array has "show-version" rules.
 * @param versions - Array of CatalogicalItem versions to check
 * @returns true if any version has a "show-version" rule in its specifications
 */
export const hasAnyShowVersionRules = (versions: CatalogicalItem[]): boolean => {
  return versions.some((version) => {
    const rules = getItemRules(version);
    return hasShowVersionRule(rules);
  });
};

/**
 * Gets only the "show-version" rules from a set of rules
 * @param rules - Array of RuleProperties to filter
 * @returns Array of RuleProperties that have the name "show-version"
 */
export const getShowVersionRules = (rules: RuleProperties[]): RuleProperties[] => {
  return rules.filter((rule) => rule.name === SHOW_VERSION_RULE_NAME);
};

/**
 * Evaluates "show-version" rules for a single version against user info using json-rules-engine.
 * @param showVersionRules - The "show-version" rules to evaluate
 * @param item - The catalogical item (version) being evaluated
 * @param userInfo - The current user's information
 * @returns Promise that resolves to true if the user passes the "show-version" rules
 */
const evaluateShowVersionRules = async (
  showVersionRules: RuleProperties[],
  item: CatalogicalItem,
  userInfo: AmazonianInformation
): Promise<boolean> => {
  try {
    const engine = RulesEngineFactory.getRulesEngine(showVersionRules);
    const facts: Record<string, any> = { item, userInfo };
    const result = await engine.run(facts);
    return result.events.length > 0;
  } catch (error) {
    console.error("Failed to evaluate show-version rules for version:", item.taxonomyId, error);
    return false;
  }
};

/**
 * Asynchronously filters versions based on "show-version" rules using json-rules-engine.
 *
 * Logic:
 * 1. For each version, extract its rules and check for "show-version" rules
 * 2. Evaluate "show-version" rules against the current user
 * 3. If 1 or more versions have a "show-version" rule AND the user passes at least one:
 * → Show only the versions where the user passes the "show-version" rule
 * 4. If no versions have "show-version" rules, OR the user doesn't pass any:
 * → Show all versions that don't have "show-version" rules
 *
 * @param versions - Array of CatalogicalItem versions to filter
 * @param userInfo - The current user's information for rule evaluation
 * @returns Promise resolving to filtered array of CatalogicalItem versions
 */
export const filterVersionsByShowVersionRules = async (
  versions: CatalogicalItem[],
  userInfo: AmazonianInformation
): Promise<CatalogicalItem[]> => {
  if (!versions || versions.length === 0) {
    return versions;
  }

  // Step 1: For each version, extract rules and identify which have "show-version" rules
  const versionRulesMap = versions.map((version) => {
    const rules = getItemRules(version);
    const showVersionRules = getShowVersionRules(rules);
    return {
      version,
      showVersionRules,
      hasShowVersion: showVersionRules.length > 0,
    };
  });

  // Check if any version has "show-version" rules
  const anyVersionHasShowVersionRule = versionRulesMap.some((entry) => entry.hasShowVersion);

  if (!anyVersionHasShowVersionRule) {
    // No versions have "show-version" rules, return all versions as-is
    return versions;
  }

  // Step 2: Evaluate "show-version" rules for versions that have them
  const evaluationResults = await Promise.all(
    versionRulesMap.map(async (entry) => {
      if (!entry.hasShowVersion) {
        return { ...entry, userPasses: false };
      }
      const userPasses = await evaluateShowVersionRules(entry.showVersionRules, entry.version, userInfo);
      return { ...entry, userPasses };
    })
  );

  // Step 3: Check if the user passes any "show-version" rule
  const userPassesAnyShowVersionRule = evaluationResults.some((entry) => entry.hasShowVersion && entry.userPasses);

  if (userPassesAnyShowVersionRule) {
    // Show only versions where the user passes the "show-version" rule
    return evaluationResults.filter((entry) => entry.hasShowVersion && entry.userPasses).map((entry) => entry.version);
  } else {
    // User doesn't pass any "show-version" rule
    // Show all versions that don't have "show-version" rules
    return evaluationResults.filter((entry) => !entry.hasShowVersion).map((entry) => entry.version);
  }
};
